/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { FileText } from 'lucide-react';
import { FC } from 'react';
import { TriageAgentHistoryLine, TriageResultConclusionItem } from 'triage-agent/types';

interface TriageTabProps {
    history: TriageAgentHistoryLine[];
    conclusion: TriageResultConclusionItem[];
}

const getFunctionLabel = (functionName: string) => {
    switch (functionName) {
        case 'analyzeLogs':
            return 'Logs';
        case 'analyzeTraces':
            return 'Traces';
        case 'analyzeScreenshots':
            return 'Screenshots';
        case 'stop':
            return 'Stop';
        case 'analyzeSteps':
            return 'Steps';
        default:
            return functionName;
    }
};

export const TriageTab: FC<TriageTabProps> = ({ history, conclusion }) => {
    return (
        <div className="">
            <div className="flex items-center gap-2 mb-4">
                <FileText className="text-slate-600 w-5" />
                <h3 className="text-lg font-medium text-slate-800">Conclusion</h3>
            </div>
            {conclusion && conclusion.length > 0 ? (
                <div className="prose prose-slate max-w-none dark:prose-invert bg-slate-50 rounded p-4">
                    {conclusion.map((conclusionItem, conclusionIndex) => (
                        <div key={conclusionIndex} className="mb-6">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-sm font-bold text-slate-600">Step {conclusionItem.stepRank}</span>
                            </div>
                            <div className="space-y-4">
                                {conclusionItem.items.map((item, index) => (
                                    <div key={index} className="bg-white rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm font-medium text-slate-600">
                                                {getFunctionLabel(item.function)}
                                            </span>
                                        </div>
                                        {item.evidence.map((evidence, evidenceIndex) => (
                                            <p key={evidenceIndex} className="text-slate-700 mb-2 last:mb-0">
                                                {evidence}
                                            </p>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center gap-4 p-8">
                    <p className="text-slate-500">No conclusion data available</p>
                </div>
            )}
            {history && history.length > 0 ? (
                <div className="mt-8">
                    <div className="flex items-center gap-2 mb-4">
                        <FileText className="text-slate-600 w-5" />
                        <h3 className="text-lg font-medium text-slate-800">History</h3>
                    </div>
                    <div className="prose prose-slate max-w-none dark:prose-invert bg-slate-50 rounded p-4">
                        {history.map((item, index) => (
                            <div key={index} className="mb-6 last:mb-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-bold text-slate-600">{item.function}</span>
                                </div>
                                <div className="bg-white rounded-lg p-4">
                                    <div className="mb-4">
                                        <p className="text-sm font-medium text-slate-600 mb-1">Arguments:</p>
                                        <pre
                                            className="text-sm text-slate-700 bg-slate-100 p-2 rounded
                                            whitespace-pre-wrap break-words"
                                        >
                                            {JSON.stringify(JSON.parse(item.args), null, 2)}
                                        </pre>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-slate-600 mb-1">Result:</p>
                                        <p className="text-slate-700 whitespace-pre-wrap">{item.result}</p>
                                    </div>
                                </div>
                                {item.screenshots && (
                                    <div className="grid grid-cols-2 gap-4 my-4">
                                        <div className="bg-white rounded-lg p-4">
                                            <p className="text-sm font-medium text-slate-600 mb-2">Failing</p>
                                            <img
                                                src={item.screenshots.failing}
                                                alt="Failing screenshot"
                                                className="rounded-lg border border-slate-200"
                                            />
                                        </div>
                                        <div className="bg-white rounded-lg p-4">
                                            <p className="text-sm font-medium text-slate-600 mb-2">Passing</p>
                                            <img
                                                src={item.screenshots.passing}
                                                alt="Passing screenshot"
                                                className="rounded-lg border border-slate-200"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-4 p-8">
                    <p className="text-slate-500">No history data available</p>
                </div>
            )}
        </div>
    );
};
